// conversationAdapter/toolCallDetail — the F124 boundary codec: a tool call's
// close, as raw wire JSON, → the UI's `ToolStepOutcome`.
//
// Server tools hand the PM a typed `ToolCallDetail` that is stored on
// `tool_call_completed` (and folded onto the state's `tool_call_result`
// entry); failures carry the typed `ToolFailureReason`. Both the live SSE
// event and the reloaded entry outcome use the SAME field names, so one
// parser serves both paths. Parse, don't validate: an unknown shape degrades
// to "no detail" (the step renders as before), never a guess, never a throw.
// We never re-read ids out of the agent-facing `output` text.

export { toToolStepOutcome };

import type {
    ToolCallDetail,
    ToolFailure,
    ToolFailureCategory,
    ToolReadLocator,
    ToolResourceKind,
    ToolResourceLink,
    ToolStepOutcome,
} from "../../components/Chat/types.js";
import { fields, list, num, str } from "../wireShape.js";

const RESOURCE_KINDS: readonly ToolResourceKind[] = [
    "file",
    "source",
    "material",
    "export",
    "deleted",
];

const FAILURE_CATEGORIES: readonly ToolFailureCategory[] = [
    "timeout",
    "network",
    "provider_busy",
    "permission_denied",
    "quota_exhausted",
    "invalid_arguments",
    "context_window_exceeded",
    "source_content_missing",
    "adapter_crashed",
    "other",
];

const toLink = (raw: unknown): ToolResourceLink | undefined => {
    const f = fields(raw);
    const id = str(f?.get("id"));
    const title = str(f?.get("title"));
    const kindText = str(f?.get("kind"));
    const kind = RESOURCE_KINDS.find((k) => k === kindText);
    return id === undefined || title === undefined || kind === undefined
        ? undefined
        : { id, kind, title };
};

// A read locator; an unknown or malformed one is simply absent (the row then
// names the resource without the part).
const toLocator = (raw: unknown): ToolReadLocator | undefined => {
    const f = fields(raw);
    const first = num(f?.get("first"));
    const last = num(f?.get("last"));
    if (first === undefined || last === undefined) return undefined;
    switch (str(f?.get("type"))) {
        case "page_range":
            return { type: "page_range", first, last };
        case "section_range":
            return { type: "section_range", first, last };
        default:
            return undefined;
    }
};

const toDetail = (raw: unknown): ToolCallDetail | undefined => {
    const f = fields(raw);
    switch (str(f?.get("type"))) {
        case "resource_touched": {
            const resource = toLink(f?.get("resource"));
            if (resource === undefined) return undefined;
            const locator = toLocator(f?.get("locator"));
            return locator === undefined
                ? { type: "resource_touched", resource }
                : { type: "resource_touched", resource, locator };
        }
        case "parts_planned": {
            const root = toLink(f?.get("root"));
            return (
                root && {
                    type: "parts_planned",
                    root,
                    parts: list(f?.get("parts")).flatMap((p) => {
                        const link = toLink(p);
                        return link === undefined ? [] : [link];
                    }),
                }
            );
        }
        case "citation_added": {
            const source = toLink(f?.get("source"));
            return source && { type: "citation_added", source };
        }
        case "package_exported": {
            const pkg = toLink(f?.get("package"));
            return pkg && { type: "package_exported", package: pkg };
        }
        case "source_still_processing": {
            const source = toLink(f?.get("source"));
            return source && { type: "source_still_processing", source };
        }
        case "resources_listed":
            return {
                type: "resources_listed",
                resources: list(f?.get("resources")).flatMap((r) => {
                    const link = toLink(r);
                    return link === undefined ? [] : [link];
                }),
            };
        case "recorded_claim": {
            const wording = str(f?.get("wording"));
            if (wording === undefined) return undefined;
            return {
                type: "recorded_claim",
                wording,
                backing: list(f?.get("backing")).flatMap((b) => {
                    const g = fields(b);
                    const source = toLink(g?.get("source"));
                    const page = num(g?.get("page"));
                    if (source === undefined) return [];
                    return page === undefined
                        ? [{ source }]
                        : [{ source, page }];
                }),
            };
        }
        default:
            return undefined;
    }
};

// The server's `source_content_unavailable` wire (JSON in the failure's
// `detail`) names the source whose content is missing. Parsed only for the
// `source_content_missing` category; anything malformed → undefined (the row
// still names the reason, just without the re-upload action).
const missingSourceOf = (
    detail: string | undefined,
): ToolResourceLink | undefined => {
    if (detail === undefined) return undefined;
    let raw: unknown;
    try {
        raw = JSON.parse(detail);
    } catch {
        return undefined;
    }
    const f = fields(raw);
    if (str(f?.get("kind")) !== "source_content_unavailable") return undefined;
    const id = str(f?.get("sourceId"));
    const title = str(f?.get("sourceTitle"));
    return id === undefined || title === undefined
        ? undefined
        : { id, kind: "source", title };
};

const toFailure = (raw: unknown): ToolFailure | undefined => {
    const f = fields(raw);
    switch (str(f?.get("type"))) {
        case "unknown_tool":
            return { type: "unknown_tool" };
        case "interrupted_by_restart":
            return { type: "interrupted_by_restart" };
        case "adapter_failed": {
            const categoryText = str(f?.get("category"));
            const category = FAILURE_CATEGORIES.find((c) => c === categoryText);
            if (category === undefined) return undefined;
            const detail = str(f?.get("detail"));
            const missing =
                category === "source_content_missing"
                    ? missingSourceOf(detail)
                    : undefined;
            if (missing !== undefined) {
                return { type: "source_content_missing", source: missing };
            }
            return detail === undefined
                ? { type: "adapter_failed", category }
                : { type: "adapter_failed", category, detail };
        }
        default:
            return undefined;
    }
};

// `f` is either the live `tool_call_completed` / `tool_call_failed` event or a
// reloaded entry's `outcome`. Any other type is not a close: `undefined`.
const toToolStepOutcome = (
    f: ReadonlyMap<string, unknown> | undefined,
): ToolStepOutcome | undefined => {
    switch (str(f?.get("type"))) {
        case "tool_call_completed": {
            const detail = toDetail(f?.get("detail"));
            return detail === undefined
                ? { type: "completed" }
                : { type: "completed", detail };
        }
        case "tool_call_failed": {
            const reason = toFailure(f?.get("reason"));
            return reason === undefined
                ? { type: "failed" }
                : { type: "failed", reason };
        }
        default:
            return undefined;
    }
};
